/**
 * rerenderAfterAutismAppointmentDateChange
 * ----------------------------------------
 * Adult Autism counterpart to rerenderAfterAppointmentDateChange.js (ADHD).
 *
 * After a clinician / Support / Org Admin commits appointment-date changes on
 * the Autism Patient Overview (one dialog session may change several modules),
 * re-finalise every PDF that embeds those dates:
 *
 *   1. Stage CAR(s) — for every affected stage whose section is already
 *      *approved* (a finalised PDF exists). Stages still in the violet
 *      "AI outputs ready for review" state are skipped: the date is only baked
 *      in at render time, and nothing has been rendered for them yet.
 *   2. Patient Report — exactly once, via rerenderAutismPatientReportIfStale,
 *      after all CAR work. Multiple module changes must never fan out into
 *      multiple concurrent PR renders.
 *
 * Why a separate file rather than parameterising the ADHD helper:
 * The two data models differ in every place that matters here. ADHD approval
 * flags live under `dataModel.6_manageReports` on the patient document and
 * renders go through TriggerRenderDocTemplate with a human-readable
 * `reportStage`; Autism approval flags live on one `reportContent/{stageKey}`
 * document per stage and renders go through TriggerRenderAutismDocTemplate
 * keyed by `stageKey`. Two small parallel helpers are clearer than one
 * conditional-heavy abstraction (the same reasoning as autoAutismPatientReport).
 *
 * Module keys vs stage keys (important for juniors):
 *   The Patient Overview and the appointment-date storage work in SHORT module
 *   keys — `ddi`, `obs`, `dd`, `pad`. The report pipeline works in STAGE keys —
 *   `history`, `semiStructuredInterview`, `observation`, `diagnosticDecision`,
 *   `postAssessmentDiscussion`. One short key can fan out to several stages:
 *   `ddi` (the single Diagnostic Developmental Interview appointment) covers
 *   BOTH the History and SSI stages, each of which has its own CAR. This file
 *   does that translation using the shared grouping in adultAutismStages.js.
 *
 * Cross-stage dates:
 *   The MDT Review (DD) CAR prints the History and Observation appointment
 *   dates in its "Assessment Details" block (assessmentDateCI / assessmentDateOBS
 *   in asd_car/render.py::_prepare_dd_extra). So a change to `ddi` or `obs`
 *   also makes an already-approved DD CAR stale, and we re-render it too. The
 *   Patient Report embeds every stage's date, which the PR step covers.
 */

import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../firebase';
import {
  getPatientHeaderPathSegments,
  getPatientReportContentPathSegments,
} from '../constants/adultAutismFirestorePaths';
import {
  AUTISM_STAGE_KEYS,
  OVERVIEW_MODULE_TO_STAGE_KEYS,
  STAGE_TO_REPORT_PREFIX,
  getAutismAppointmentShortKey,
  getEnabledAutismStages,
} from '../constants/adultAutismStages';
import { resolveWorkflowConfig } from '../hooks/useWorkflowConfig';
import { TriggerRenderAutismDocTemplate } from './TriggerRenderAutismDocTemplate';
import { rerenderAutismPatientReportIfStale } from './autoAutismPatientReport';

/**
 * Short appointment key → the stage keys whose CARs print that date as their
 * OWN appointment date. Built from the overview grouping so the pairing of
 * History + SSI under `ddi` is declared in exactly one place.
 *
 *   { ddi: ['history', 'semiStructuredInterview'], obs: ['observation'],
 *     dd: ['diagnosticDecision'], pad: ['postAssessmentDiscussion'] }
 */
export const APPOINTMENT_KEY_TO_STAGE_KEYS = Object.entries(OVERVIEW_MODULE_TO_STAGE_KEYS)
  .reduce((acc, [moduleKey, stageKeys]) => {
    const shortKey = getAutismAppointmentShortKey(moduleKey);
    if (shortKey) acc[shortKey] = [...stageKeys];
    return acc;
  }, {});

/**
 * Short appointment keys whose dates are ALSO embedded in another stage's CAR.
 * Today only the DD CAR does this (History + Observation dates in its
 * Assessment Details block). Keep in step with _prepare_dd_extra in
 * python-functions/functions/asd_car/render.py.
 */
export const CROSS_STAGE_DATE_DEPENDENTS = {
  ddi: ['diagnosticDecision'],
  obs: ['diagnosticDecision'],
};

/** Stage key → the short appointment key it belongs to (inverse of the map above). */
const STAGE_KEY_TO_APPOINTMENT_KEY = Object.entries(APPOINTMENT_KEY_TO_STAGE_KEYS)
  .reduce((acc, [shortKey, stageKeys]) => {
    stageKeys.forEach((stageKey) => { acc[stageKey] = shortKey; });
    return acc;
  }, {});

/**
 * Normalise callers that pass either a single moduleKey or a moduleKeys array.
 * Dedupes and drops unknown keys so a stray ADHD key (`ci`) can never reach the
 * Autism render path.
 */
function resolveModuleKeys(moduleKey, moduleKeys) {
  const raw = Array.isArray(moduleKeys)
    ? moduleKeys
    : (moduleKey ? [moduleKey] : []);
  const seen = new Set();
  const resolved = [];
  for (const key of raw) {
    if (!APPOINTMENT_KEY_TO_STAGE_KEYS[key] || seen.has(key)) continue;
    seen.add(key);
    resolved.push(key);
  }
  return resolved;
}

/** Is this stage's section report approved (i.e. a finalised PDF exists)? */
const isStageApproved = (reportContent, stageKey) => {
  const prefix = STAGE_TO_REPORT_PREFIX[stageKey];
  return reportContent?.[stageKey]?.[`${prefix}ReportApproved`] === true;
};

/**
 * Work out which stage CARs must be re-rendered for a set of changed short
 * keys, preserving canonical stage order and honouring the org's enabled
 * stages (an LMP patient never has an Observation CAR to refresh).
 *
 * @returns {string[]} stage keys, in AUTISM_STAGE_KEYS order
 */
function collectAffectedStageKeys(shortKeys, reportContent, enabledStageKeys) {
  const candidates = new Set();
  for (const shortKey of shortKeys) {
    (APPOINTMENT_KEY_TO_STAGE_KEYS[shortKey] || []).forEach((s) => candidates.add(s));
    (CROSS_STAGE_DATE_DEPENDENTS[shortKey] || []).forEach((s) => candidates.add(s));
  }
  return AUTISM_STAGE_KEYS.filter(
    (stageKey) =>
      candidates.has(stageKey) &&
      enabledStageKeys.includes(stageKey) &&
      isStageApproved(reportContent, stageKey)
  );
}

/**
 * Predict which report artefacts will actually be re-finalised after the given
 * appointment-date changes, using the SAME gates the background re-render
 * applies. The dialog uses this so its confirm/success copy and the
 * "Go to Documents Dashboard" CTA only appear when a re-finalise will happen.
 *
 * @param {Object} params
 * @param {Object} [params.reportContent]     stageKey → reportContent doc (from ModularPatientDataContext)
 * @param {Object} [params.header]            patient header doc (for autismPatientReport.*)
 * @param {string[]} [params.moduleKeys]      short keys being changed this session (ddi/obs/dd/pad)
 * @param {string[]} [params.enabledStageKeys] stages active for this patient's workflow;
 *                                             defaults to all five
 * @returns {{
 *   carsToRefinalise: string[],          // SHORT keys (for dialog labels), canonical order
 *   stagesToRefinalise: string[],        // stage keys that will actually be rendered
 *   willRefinalisePatientReport: boolean,
 *   willRefinaliseAny: boolean,
 * }}
 */
export function predictAutismAppointmentDateReportRefinalisation({
  reportContent = {},
  header = {},
  moduleKeys = [],
  enabledStageKeys = AUTISM_STAGE_KEYS,
} = {}) {
  const keys = resolveModuleKeys(undefined, moduleKeys);
  const stagesToRefinalise = collectAffectedStageKeys(keys, reportContent, enabledStageKeys);

  // Report the affected CARs back in short-key form so the dialog can label
  // them with the same titles the overview cards use. Two stages (History and
  // SSI) collapse to the one `ddi` label; the DD CAR pulled in via the
  // cross-stage rule shows up as `dd` even if the clinician only changed `ddi`.
  const carsToRefinalise = [];
  for (const stageKey of stagesToRefinalise) {
    const shortKey = STAGE_KEY_TO_APPOINTMENT_KEY[stageKey];
    if (shortKey && !carsToRefinalise.includes(shortKey)) carsToRefinalise.push(shortKey);
  }

  // Patient Report: rerenderAutismPatientReportIfStale requires (a) every
  // enabled section approved and (b) the report to be stale. After save we bump
  // appointmentDatesLastChangedAt, which makes it stale only when a
  // prReportApprovedAt anchor already exists — the rule never bootstraps a
  // first PR, so a patient without a finalised PR gets no PR re-render here.
  const allApproved = enabledStageKeys.every((stageKey) =>
    isStageApproved(reportContent, stageKey)
  );
  const willRefinalisePatientReport =
    keys.length > 0 && allApproved && !!header?.autismPatientReport?.prReportApprovedAt;

  return {
    carsToRefinalise,
    stagesToRefinalise,
    willRefinalisePatientReport,
    willRefinaliseAny: stagesToRefinalise.length > 0 || willRefinalisePatientReport,
  };
}

/**
 * Read the patient header and every stage's reportContent doc fresh from
 * Firestore. We deliberately do not trust the caller's in-memory context here:
 * an approval could have landed since the overview last loaded.
 */
async function readFreshState(patientId) {
  const headerRef = doc(db, ...getPatientHeaderPathSegments(patientId));
  const stageRefs = AUTISM_STAGE_KEYS.map((stageKey) =>
    doc(db, ...getPatientReportContentPathSegments(patientId, stageKey))
  );
  const [headerSnap, ...stageSnaps] = await Promise.all([
    getDoc(headerRef),
    ...stageRefs.map((ref) => getDoc(ref)),
  ]);

  const header = headerSnap.exists() ? headerSnap.data() : {};
  const reportContent = {};
  AUTISM_STAGE_KEYS.forEach((stageKey, i) => {
    reportContent[stageKey] = stageSnaps[i].exists() ? stageSnaps[i].data() : null;
  });
  return { header, reportContent };
}

/**
 * Wait for a stage's CAR render to leave 'processing'. Autism stage statuses
 * live on the reportContent doc (not the patient header), so the shared
 * waitForProcessingComplete (which listens to the patient doc) cannot be
 * reused. Resolves with the final status, or null on timeout / error.
 */
function waitForStageRenderComplete(patientId, stageKey, timeoutMs) {
  const prefix = STAGE_TO_REPORT_PREFIX[stageKey];
  const statusField = `${prefix}ReportProcessingStatus`;
  const ref = doc(db, ...getPatientReportContentPathSegments(patientId, stageKey));

  return new Promise((resolve) => {
    // `unsub` is assigned by onSnapshot's return value, but a listener can be
    // invoked synchronously (cached data) before that assignment lands, so
    // every exit path goes through `settle`, which unsubscribes once the
    // handle exists and otherwise defers the unsubscribe to just after.
    let unsub = null;
    let settled = false;
    const settle = (value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      if (unsub) unsub();
      resolve(value);
    };

    const timeout = setTimeout(() => settle(null), timeoutMs);

    unsub = onSnapshot(
      ref,
      (snap) => {
        const status = snap.data()?.[statusField];
        if (status !== 'processing') settle(status ?? null);
      },
      (error) => {
        console.error('waitForStageRenderComplete snapshot error:', error);
        settle(null);
      }
    );
    // Listener fired synchronously before `unsub` existed: detach now.
    if (settled && unsub) unsub();
  });
}

/**
 * Re-finalise the Autism PDFs affected by appointment-date changes.
 *
 * Wait-then-retry contract (same as the ADHD helper's waitAndRetry): if a
 * stage CAR is already rendering when we arrive (a clinician approved it
 * seconds ago), that in-flight render read the OLD date. We wait for it to
 * finish, then trigger once more so the new date is captured. We never queue
 * more than one retry per stage.
 *
 * @param {Object} params
 * @param {string} params.patientId
 * @param {string} [params.moduleKey]    single short key (one-module callers)
 * @param {string[]} [params.moduleKeys] preferred: all short keys changed this session
 * @param {string} [params.clinicianId]  defaults to auth.currentUser.uid
 * @returns {Promise<{ success: boolean, cars?: Object, pr?: Object, skipped?: string }>}
 *   `cars` is keyed by STAGE key (one entry per stage render attempted/skipped).
 */
export async function rerenderAfterAutismAppointmentDateChange({
  patientId,
  moduleKey,
  moduleKeys,
  clinicianId,
}) {
  const uid = clinicianId || auth.currentUser?.uid;
  const keys = resolveModuleKeys(moduleKey, moduleKeys);

  if (!patientId || !uid || keys.length === 0) {
    return { success: false, skipped: 'missing-params' };
  }

  let header = {};
  let reportContent = {};
  try {
    ({ header, reportContent } = await readFreshState(patientId));
  } catch (err) {
    console.error('rerenderAfterAutismAppointmentDateChange: failed to read patient state', err);
  }

  const wf = resolveWorkflowConfig(header, { organisation: header?.organisation });
  const enabledStageKeys = getEnabledAutismStages(wf);
  const stagesToRender = collectAffectedStageKeys(keys, reportContent, enabledStageKeys);

  const results = { success: true, cars: {} };

  // 12 min: comfortably longer than the render CF's 6 min client timeout, and
  // matching the Patient Report waiter in autoAutismPatientReport.
  const WAIT_TIMEOUT_MS = 12 * 60 * 1000;

  // ── Stage CARs (sequential so we never hammer the render CF) ─────────────
  for (const stageKey of stagesToRender) {
    const prefix = STAGE_TO_REPORT_PREFIX[stageKey];
    try {
      if (reportContent?.[stageKey]?.[`${prefix}ReportProcessingStatus`] === 'processing') {
        console.log(`Autism ${stageKey} CAR already rendering — waiting before re-render`);
        await waitForStageRenderComplete(patientId, stageKey, WAIT_TIMEOUT_MS);
      }
      results.cars[stageKey] = await TriggerRenderAutismDocTemplate({
        patientId,
        stageKey,
        clinicianId: uid,
      });
    } catch (err) {
      console.error(`rerenderAfterAutismAppointmentDateChange: CAR re-render failed (${stageKey})`, err);
      results.cars[stageKey] = { success: false, error: err?.message || String(err) };
    }
  }

  // Record skipped stages too, so callers/tests can see why nothing rendered
  // (e.g. the stage is only "ready for review", not approved).
  for (const shortKey of keys) {
    for (const stageKey of APPOINTMENT_KEY_TO_STAGE_KEYS[shortKey] || []) {
      if (!(stageKey in results.cars)) {
        results.cars[stageKey] = { success: true, skipped: true, reason: 'module-not-approved' };
      }
    }
  }

  // ── Patient Report — once only, after all CAR work ───────────────────────
  // The helper reads fresh state itself and applies its own gates (all enabled
  // sections approved; appointmentDatesLastChangedAt newer than
  // prReportApprovedAt). Even if three CARs re-rendered above, exactly one PR
  // job is ever kicked off here.
  try {
    results.pr = await rerenderAutismPatientReportIfStale({ patientId, clinicianId: uid });
  } catch (err) {
    console.error('rerenderAfterAutismAppointmentDateChange: PR re-render failed', err);
    results.pr = { success: false, error: err?.message || String(err) };
  }

  return results;
}
